import { useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { FacilityNode } from "@arc/contracts";
import type { AuthSession } from "../../../auth/keycloak.js";
import { SubtreeConfirmDialog } from "../SubtreeConfirmDialog.js";
import { useFacilityFavourites } from "../useFacilityFavourites.js";
import type { FacilityAuthz } from "../useFacilityAuthz.js";
import type { FacilityKebabMenuProps } from "../FacilityKebabMenu.js";
import { MoveFacilityDrawer } from "../MoveFacilityDrawer.js";
import { FacilityDetailsDrawer } from "../FacilityDetailsDrawer.js";
import { DeleteFacilityDialog } from "../DeleteFacilityDialog.js";
import { RenameFacilityDialog } from "../RenameFacilityDialog.js";
import { useArchiveFacility, useUnarchiveFacility, useDuplicateFacility } from "../useFacilityMutations.js";
import { useShowError } from "../../../error/ErrorProvider.js";

/** The props a rail card / drill header needs to render a wired {@link FacilityKebabMenu} (minus `tabbable`, which the
 *  host sets). `null` ⇒ the caller can't act on this node (a navigable `permission:null` ancestor) → render no kebab. */
export type KebabProps = Omit<FacilityKebabMenuProps, "tabbable">;

export interface DrilldownActions {
  favorites: ReadonlySet<number>;
  /** Toggle a facility's favorite state — driven by the card's quick star AND the kebab item (one source). */
  onToggleFavorite: (id: number) => void;
  /** Build the kebab props for a node. `isCurrent` = the drill HEADER's node (its Insert-above is suppressed — a sibling
   *  of the current node belongs to the PARENT level; use a child card's kebab instead). Returns null when the caller
   *  can't act (no manage/move/archive/duplicate/insert) → the host renders no kebab. */
  getKebabProps: (node: FacilityNode, opts?: { isCurrent?: boolean }) => KebabProps | null;
  /** Restore an archived facility + its subtree (the Archived list's Unarchive button — offered on subtree ROOTS only). */
  requestUnarchive: (node: FacilityNode) => void;
  /** Add a ROOT facility (the root-list "Add facility" button — bypass/empty realm). */
  requestCreateRoot: () => void;
  /** The parent an inline create card is open under (null = a realm root), or null. */
  createUnder: { parentId: number | null } | null;
  onCreated: (name: string) => void;
  onCancelCreate: () => void;
  /** All action drawers/dialogs + the polite aria-live region. Render once near the drill-down root. */
  portals: ReactNode;
}

/**
 * Facility MANAGEMENT for the drill-down navigator (FN3 / ADR-014) — the counterpart to the classic `FacilitySelection`
 * orchestration, but with **navigation** reactions instead of expand/selection ones. Owns every action's open-state +
 * the archive/duplicate mutation handlers (rename/move/delete/manage live in their own drawers/dialogs) and returns the
 * portal JSX. The load-bearing authz comes from the shared {@link useFacilityAuthz} (one source of truth with classic);
 * this hook only wires handlers + decides the **landing view** after each op (the folded plan Q2):
 * - **Move/Archive/Delete the CURRENT node** → navigate to its parent (`drillTo(parentId)`) so you're never staring at a
 *   node that just vanished; the same op on a CHILD → stay (it leaves the current rail on refetch).
 * - **Duplicate** creates a sibling of the target → land on the sibling's parent (`drillTo(targetParent)`) UNLESS that's
 *   already the current level (a child-card duplicate → the copy lands in the current rail → stay). Never a silent no-op.
 * - **Add-child / Create-root / Rename / Manage** → stay (the change appears in place on refetch).
 */
export function useDrilldownActions(
  session: AuthSession | null,
  flat: readonly FacilityNode[],
  authz: FacilityAuthz,
  nav: { selectedId: number | null; drillTo: (id: number | null) => void },
): DrilldownActions {
  // These strings are LIVE-REGION announcements — the only feedback a screen-reader user gets that an archive,
  // move or delete actually happened. Leaving them in English is not a cosmetic gap for a German user; it is the
  // confirmation itself, in a language they may not read.
  const { t } = useTranslation();
  const { selectedId, drillTo } = nav;
  const archiveMut = useArchiveFacility(session);
  const unarchiveMut = useUnarchiveFacility(session);
  const duplicateMut = useDuplicateFacility(session);
  const showError = useShowError();
  // Persisted favourites (F1) — server-backed, optimistic. Replaces the old client-only Set.
  const favs = useFacilityFavourites(session);

  const [renaming, setRenaming] = useState<FacilityNode | null>(null);
  const [managingId, setManagingId] = useState<number | null>(null);
  const [moveSource, setMoveSource] = useState<FacilityNode | null>(null);
  const [confirm, setConfirm] = useState<{ kind: "archive" | "unarchive"; node: FacilityNode } | null>(null);
  const [duplicating, setDuplicating] = useState<FacilityNode | null>(null);
  const [deleting, setDeleting] = useState<FacilityNode | null>(null);
  const [createUnder, setCreateUnder] = useState<{ parentId: number | null } | null>(null);
  const [announce, setAnnounce] = useState("");
  // Synchronous guard for the additive, non-idempotent Duplicate POST — rejects a same-tick double-activation before
  // `isPending` re-renders (mirror of classic; a render-snapshot `isDisabled` is insufficient alone).
  const duplicateSubmitting = useRef(false);

  // ── Post-op navigation (the folded plan Q2) ────────────────────────────────────────────────────────────────────
  // The target vanished/relocated: if it was the CURRENT drilled-into node, follow to its (new) parent; else stay.
  const afterCurrentGone = (node: FacilityNode, newParentId: number | null) => {
    if (node.id === selectedId) drillTo(newParentId);
  };
  // A sibling of `target` was created (duplicate/insert-above): its parent is `target.parentId`. Land there UNLESS it's
  // already the level on screen (child-card op → the sibling is already in the current rail → stay).
  const afterSiblingCreate = (targetParentId: number | null) => {
    if (targetParentId !== selectedId) drillTo(targetParentId);
  };

  // ── Handlers ───────────────────────────────────────────────────────────────────────────────────────────────────
  // Archive (from the browse kebab) and Unarchive (from the Archived list) share the one confirm dialog, keyed by kind.
  const onConfirmLifecycle = () => {
    if (!confirm) return;
    const { kind, node } = confirm;
    if (kind === "archive") {
      if (archiveMut.isPending) return;
      archiveMut.mutate(node.id, {
        onSuccess: () => {
          setAnnounce(t("facilitySelection.announce.archived", { name: node.name }));
          setConfirm(null);
          afterCurrentGone(node, node.parentId);
        },
        onError: (err) => {
          setConfirm(null);
          showError(err);
        },
      });
      return;
    }
    // Unarchive: restores the node + its whole archived subtree; it just leaves the Archived list on refetch (no nav).
    if (unarchiveMut.isPending) return;
    unarchiveMut.mutate(node.id, {
      onSuccess: () => {
        setAnnounce(t("facilitySelection.announce.unarchived", { name: node.name }));
        setConfirm(null);
      },
      onError: (err) => {
        setConfirm(null);
        showError(err);
      },
    });
  };

  const onConfirmDuplicate = () => {
    if (!duplicating || duplicateSubmitting.current) return;
    duplicateSubmitting.current = true;
    const node = duplicating;
    duplicateMut.mutate(node.id, {
      onSuccess: () => {
        setAnnounce(t("facilitySelection.announce.duplicated", { name: node.name }));
        setDuplicating(null);
        afterSiblingCreate(node.parentId); // the copy is a sibling of `node`
      },
      onError: (err) => {
        setDuplicating(null);
        showError(err);
      },
      onSettled: () => {
        duplicateSubmitting.current = false;
      },
    });
  };

  const getKebabProps = (node: FacilityNode, opts?: { isCurrent?: boolean }): KebabProps | null => {
    const canManage = authz.bypass || node.permission === "manage" || node.permission === "access_manage";
    const canArchive = authz.manageSubtreeIds.has(node.id);
    const canDuplicate = authz.canDuplicateIds.has(node.id);
    // The current node's own "add a sibling" belongs to the PARENT rail — suppress it on the header (reach it from the
    // node's child-card at the parent level instead); no capability is lost (panel #2).
    const canInsertAbove = !opts?.isCurrent && authz.canInsertAboveIds.has(node.id);
    const movable = authz.movableIds.has(node.id);
    if (!(canManage || canArchive || canDuplicate || canInsertAbove || movable)) return null;
    return {
      node,
      isFavorite: favs.favourites.has(node.id),
      canManage,
      canArchive,
      canDuplicate,
      canInsertAbove,
      movable,
      onRename: () => setRenaming(node),
      // Add child = create UNDER this node → its create card lives at THIS node's level, so drill in first (unless we're
      // already there). Insert-above (below) targets the node's PARENT = the current level, so it needs no navigation.
      onAddChild: () => {
        if (node.id !== selectedId) drillTo(node.id);
        setCreateUnder({ parentId: node.id });
      },
      onManage: () => setManagingId(node.id),
      onMoveTo: () => setMoveSource(node),
      onArchive: () => setConfirm({ kind: "archive", node }),
      onDuplicate: () => setDuplicating(node),
      // Insert above = a sibling = a child of this node's parent (only offered off child cards → parent is the current level).
      onInsertAbove: () => setCreateUnder({ parentId: node.parentId }),
      onDelete: () => setDeleting(node),
      onToggleFavorite: () => favs.toggle(node.id),
    };
  };

  // The facility whose details drawer is open — derived fresh so its counts/permission track refetches. `canManage` is
  // the facility-management gate (manage/access_manage/bypass — the SAME derivation as the kebab, never the manuals
  // boolean; ADR-016 panel #1), which drives Manage mode inside the drawer.
  const managingFacility = useMemo(() => {
    if (managingId === null) return null;
    const node = flat.find((f) => f.id === managingId);
    if (!node) return null;
    return {
      id: node.id,
      name: node.name,
      childCount: flat.filter((f) => f.parentId === managingId).length,
      categoryCount: node.categoryCount ?? 0,
      permission: node.permission,
      canManage: authz.bypass || node.permission === "manage" || node.permission === "access_manage",
    };
  }, [flat, managingId, authz.bypass]);

  const portals = (
    <>
      {renaming ? (
        <RenameFacilityDialog
          node={renaming}
          session={session}
          onClose={() => setRenaming(null)}
          onRenamed={(name) => setAnnounce(t("facilitySelection.announce.renamed", { name }))}
        />
      ) : null}

      {moveSource ? (
        <MoveFacilityDrawer
          source={moveSource}
          flat={flat}
          bypass={authz.bypass}
          session={session}
          onClose={() => setMoveSource(null)}
          onMoved={(sourceName, destName, newParentId) => {
            setAnnounce(t("facilitySelection.announce.moved", { source: sourceName, destination: destName }));
            afterCurrentGone(moveSource, newParentId); // follow the CURRENT facility to its new home; a child just leaves the rail
          }}
        />
      ) : null}

      {managingFacility ? (
        <FacilityDetailsDrawer
          facilityId={managingFacility.id}
          facilityName={managingFacility.name}
          childCount={managingFacility.childCount}
          categoryCount={managingFacility.categoryCount}
          permission={managingFacility.permission}
          canManage={managingFacility.canManage}
          canManageManuals={managingFacility.canManage}
          session={session}
          initialMode="manage"
          onClose={() => setManagingId(null)}
        />
      ) : null}

      {confirm ? (
        <SubtreeConfirmDialog
          kind={confirm.kind}
          node={confirm.node}
          session={session}
          isPending={confirm.kind === "archive" ? archiveMut.isPending : unarchiveMut.isPending}
          onConfirm={onConfirmLifecycle}
          onCancel={() => setConfirm(null)}
        />
      ) : null}

      {duplicating ? (
        <SubtreeConfirmDialog
          kind="duplicate"
          node={duplicating}
          session={session}
          isPending={duplicateMut.isPending}
          onConfirm={onConfirmDuplicate}
          onCancel={() => setDuplicating(null)}
        />
      ) : null}

      {deleting ? (
        <DeleteFacilityDialog
          facility={{ id: deleting.id, name: deleting.name }}
          session={session}
          onClose={() => setDeleting(null)}
          onDeleted={(name) => {
            setAnnounce(t("facilitySelection.announce.deleted", { name }));
            afterCurrentGone(deleting, deleting.parentId);
          }}
        />
      ) : null}

      <p role="status" aria-live="polite" className="sr-only">
        {announce}
      </p>
    </>
  );

  return {
    favorites: favs.favourites,
    onToggleFavorite: favs.toggle,
    getKebabProps,
    requestUnarchive: (node) => setConfirm({ kind: "unarchive", node }),
    requestCreateRoot: () => setCreateUnder({ parentId: null }),
    createUnder,
    onCreated: (name) => {
      setAnnounce(t("facilitySelection.announce.created", { name }));
      setCreateUnder(null);
    },
    onCancelCreate: () => setCreateUnder(null),
    portals,
  };
}
